import React, {useContext, useEffect, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import formBuilderService from "../../../../../services/web/form-builder-service.js";
import {FormBuilderContext} from "../context/form-builder-context.jsx";
import corporationService from "../../../../../services/web/corporation-service.js";
import {arrayMove} from "@dnd-kit/sortable"
import {DndContext, DragOverlay, PointerSensor, pointerWithin, useSensor, useSensors} from "@dnd-kit/core"
import toolboxItems from "../resources/toolbox-items.js";
import FormBuilderItens from "./form-builder-itens.jsx";
import "../css/form-builder-css.css"
import FormBuilderComponentsType from "./form-builder-components-type.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import FormBuilderEditParameters from "./form-builder-edit-parameters.jsx";
import FormBuilderCards from "./form-builder-cards.jsx";
import Swal from 'sweetalert2'
import withReactContent from "sweetalert2-react-content";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import FormBuilderPreview from "./form-builder-preview.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import {FaFileExport, FaFileImport} from "react-icons/fa";
import {toast} from "react-toastify";
import {navigateHook} from "../../../../../hooks/use-navigate.js";

const API_URL = import.meta.env.VITE_API_URL;
const MySwal = withReactContent(Swal);

const FormBuilder = () => {
    const {structure, setStructure, selectItemId, handleSelectItem, setErrorList} = useContext(FormBuilderContext)

    const navigate = navigateHook();

    const {id} = useParams()
    const [formName, setFormName] = useState("")
    const [formModule, setFormModule] = useState(undefined)
    const [activeId, setActiveId] = useState(null)
    const [headerTemplate, setHeaderTemplate] = useState({})
    const [isLoading, setIsLoading] = useState(false)

    const activationConstraint = {distance: 3}
    const sensors = useSensors(useSensor(PointerSensor, {activationConstraint}))

    const {data, refetch} = useQuery({
        queryFn: () => formBuilderService.get(id).then((data) => {
            setFormName(data.name)
            setFormModule(data.module)
            setStructure(data.structure)

            return data
        }),
        queryKey: ["template"],
        enabled: !!id
    })

    const {data: headerTemplates} = useQuery({
        queryFn: () => corporationService.headerTemplates().then((data) => data.map(item => ({...item, value: item.id, label: item.id === -1 ? "Padrão" : item.name}))),
        queryKey: ["headerTemplates"]
    });

    const {data: moduleList} = useQuery({
        queryFn: () => formBuilderService.modules(),
        queryKey: ["moduleList"]
    })

    useEffect(() => {
        if (!headerTemplates) return

        const headerTemplate = headerTemplates.find(item => item.id === data?.headerTemplateId) ?? headerTemplates[0]

        setHeaderTemplate(headerTemplate)
    }, [headerTemplates, data])

    const onDragStart = ({active}) => setActiveId(active.id)

    const onDragOver = (event) => {
        const { active, over } = event
        if (!over || !active) {
            if (active.id.includes("toolbox-")) {
                setStructure(prev => prev.filter(item => item.id !== ("temp-" + active.id)))
            }
            return
        }

        const isToolboxItem = active.id.includes("toolbox-")
        const tempId = "temp-" + active.id
        const activeId = isToolboxItem ? "temp-" + active.id : active.id

        const activeIndex = structure.findIndex((item) => item.id === (isToolboxItem ? tempId : active.id))
        const overIndex = structure.findIndex((item) => item.id === over.id)

        if (isToolboxItem) {
            const isAlreadyInStructure = structure.some(item => item.id === tempId)

            if (isAlreadyInStructure) {
                if (tempId !== over.id) {
                    setStructure(arrayMove(structure, activeIndex, overIndex))
                }
            } else {
                const find = toolboxItems.find(item => item.id === active.id)
                const item = {...find, id: tempId, type: find.id.replace("toolbox-", "")}

                setStructure(prev => {
                    const newStructure = [...prev];
                    if (overIndex >= 0) {
                        newStructure.splice(overIndex, 0, item);
                    } else {
                        newStructure.unshift(item);
                    }

                    return newStructure;
                });
            }

            return
        }

        if (activeId !== over.id) {
            setStructure(prev => arrayMove(prev, activeIndex, overIndex))
        }
    }

    const onDragEnd = (event) => {
        const { over, active } = event

        if (!over || !active) return

        if (active.id.includes("toolbox-")) {
            const droppedItem = toolboxItems.find(i => i.id === active.id)
            if (!droppedItem) return

            const tempId = "temp-" + active.id
            const id = `${droppedItem.id.replace("toolbox-", "")}-${Date.now()}`

            handleSelectItem(id)
            setStructure(prev => prev.map(item => item.id === tempId ? {...item, id} : item))
        }

        setActiveId(null)
    }

    const dragOverlayContent = () => {
        if (activeId && activeId.includes("toolbox")) {
            const find = toolboxItems.find(item => item.id === activeId)
            return <FormBuilderItens item={find}/>
        }

        const find = structure.find(i => i.id === activeId)
        if (find) return <FormBuilderComponentsType item={find} />
    }

    const submit = (e) => {
        e.preventDefault()

        const structure_ = structure.map(item => {
            if (!["checkbox", "radio"].includes(item.type)) return item
            return {...item, options: (item.options ?? []).filter(opt => opt.trim() !== "")}
        })

        const form = new FormData();
        form.append("name", formName);
        form.append("module", formModule ?? null);
        form.append("headerTemplateId", headerTemplate.value);
        form.append("structure", JSON.stringify(structure_));

        structure.filter(item => item.type === "image" && item.file).forEach((item) => form.append("files", item.file.file, item.id));

        setIsLoading(true)

        let response
        if (id ) response = formBuilderService.edit(form, id)
        else response = formBuilderService.create(form)

        response
            .then((data) => {
                projectUtils.success(!id);

                if (id) {
                    refetch();
                    return;
                }

                navigate(`/dev/form-builder/form/${data}`);
            })
            .catch(error => {
                console.error(error)
                setIsLoading(false)
                let message = "Não foi possível salvar."

                const tempErrorList = []
                const errorData = error.response.data
                if (!!errorData) {
                    if (errorData.message) message = errorI18n(errorData?.message) ?? "Não foi possível salvar."
                    else if (Object.keys(errorData).length) {
                        message = Object.keys(errorData).map(key => {
                            const find = structure.find(s => s.id === key)
                            if (find) {
                                tempErrorList.push(key)
                                return errorI18n(errorData[key], find.label)
                            }
                        }).join("<br />")
                    }
                }

                projectUtils.swalErrorMessage('', message, 'Ok')
                setErrorList(tempErrorList)
            })
            .finally(() => setIsLoading(false))
    }

    const clearForm = (e) => {
        e.preventDefault()

        handleSelectItem(null)
        setStructure([])
    }

    const deleteForm = (e) => {
        e.preventDefault()

        MySwal.fire({
            icon: "warning",
            title: "Excluir!",
            showCancelButton: true,
            text: `Deseja excluir o formulário "${formName}"?`,
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);

                formBuilderService.delete(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigate(`/dev/form-builder`)
                    })
                    .catch((error) => {
                        let swalMessage = 'Não foi possível excluir.';
                        console.error(error);
                        projectUtils.swalErrorMessage('', swalMessage, 'Ok')
                    })
            }
        });
    }

    const errorI18n = (error, value = "") => {
        const i18n = {
            "missing.name": "O campo \"Nome do formulário\" é obrigatório.",
            "missing.module": "O campo \"Local de exibição\" é obrigatório.",
            "name.already.used": "\"Nome do formulário\" já existente.",
            "missing.label": "O campo \"Label\" é obrigatório.",
            "label.already.exists": "label \"%s\" já existente.",
            "options.is.required": "Adicione uma opção ao checkbox/radio \"%s\"",
            "missing.structure": "Adicione um item no formulário"
        }

        return i18n[error].replace("%s", value)
    }

    const exportFile = () => {
        const form = {
            name: formName,
            module: formModule,
            structure: structure.filter(item => !item?.file)
        }

        const formStringify = JSON.stringify(form, null, 2)

        const jsonFile = new Blob([formStringify], { type: 'application/json' });
        const url = window.URL.createObjectURL(jsonFile);

        const a = document.createElement('a');
        a.href = url;
        a.download = formName + ".json";
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const importFile = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/json';
        input.onchange = event => {
            const file = event.target.files[0]
            if (!file) return;

            const reader = new FileReader();

            reader.onload = (e) => {
                const result = e.target.result;
                if (!result) return;

                const parse = JSON.parse(result);
                if (!parse?.name && !parse?.module && !parse?.structure){
                    toast.error("Arquivo inválido.");
                    return;
                }

                setFormName(parse?.name ?? "");
                setFormModule(parse?.module);
                setStructure(parse?.structure ?? []);
            }

            reader.readAsText(file);
        }

        input.click();
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Construtor de formulários
                </Template.Title>

                <div className={"flex gap-2"}>
                    <Button onClick={exportFile}>
                        <FaFileExport /> Exportar
                    </Button>
                    <Button onClick={importFile}>
                        <FaFileImport /> Importar
                    </Button>
                </div>
            </Template.Header>

            <Template.Body>
                <DndContext sensors={sensors} onDragStart={onDragStart} collisionDetection={pointerWithin} onDragEnd={onDragEnd} onDragOver={onDragOver}>
                    <div className="flex gap-2">
                        <div className="w-[295px]">
                            <Card className={"mb-2"}>
                                <Card.Body className={"space-y-4"}>
                                    <InputForm
                                        id={"formName"}
                                        value={formName}
                                        label="Nome do formulário *"
                                        onInput={({target: {value}}) => setFormName(value)}
                                    />

                                    <SelectComponent
                                        value={formModule}
                                        options={moduleList}
                                        label={"Local de exibição *"}
                                        menuPlacement={"bottom"}
                                        onChange={(value) => setFormModule(value?.value)}
                                    />

                                    <SelectComponent
                                        value={headerTemplate}
                                        options={headerTemplates}
                                        label={"Cabeçalho"}
                                        menuPlacement={"bottom"}
                                        isClearable={false}
                                        onChange={(value) => setHeaderTemplate(value)}
                                    />
                                </Card.Body>
                            </Card>

                            {
                                !selectItemId &&
                                <Card className={"overflow-auto max-h-[calc(100vh-253px)]"}>
                                    <Card.Body className={"grid grid-cols-2 justify-self-center gap-2"}>
                                        {
                                            toolboxItems.map(item => <FormBuilderItens key={item.id} item={item}/>)
                                        }
                                    </Card.Body>
                                </Card>
                            }

                            {
                                !!selectItemId && <FormBuilderEditParameters />
                            }
                        </div>

                        <div className="w-[calc(100%-290px)] grid grid-cols-12 gap-x-2">
                            <FormBuilderCards structure={structure}/>

                            <FormBuilderPreview formName={formName} headerTemplate={headerTemplate} structure={structure} setIsLoading={setIsLoading}/>

                            <div className="col-span-full">
                                <Card className="mt-2">
                                    <Card.Body className={"flex gap-2 justify-end"}>
                                        {
                                            id &&
                                            <Button target={"danger"} onClick={deleteForm} actionFeedbackType='delete'>

                                                Excluir
                                            </Button>
                                        }
                                        <Button target={"secondary"} onClick={clearForm}>
                                            Limpar formulário
                                        </Button>
                                        <Button target={"primary"} onClick={submit} actionFeedbackType='save'>

                                            Salvar
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </div>

                    <DragOverlay adjustScale={false}>
                        {dragOverlayContent()}
                    </DragOverlay>
                </DndContext>
            </Template.Body>
        </Template>
    );
};

export default FormBuilder;